import { useState, useEffect, useCallback, useMemo } from 'react';
import type { Flight, CreateFlightInput, UpdateFlightInput, FlightStats } from '@/types';
import {
  getFlights,
  createFlight,
  deleteFlight,
  updateFlight,
  calculateFlightStats,
} from '@/services/flightService';

interface UseFlightsResult {
  flights: Flight[];
  stats: FlightStats;
  isLoading: boolean;
  error: string | null;
  addFlight: (input: CreateFlightInput) => Promise<Flight | null>;
  removeFlight: (flightId: string) => Promise<void>;
  updateFlightDetails: (flightId: string, input: UpdateFlightInput) => Promise<Flight | null>;
  refresh: () => Promise<void>;
}

export function useFlights(userId: string = 'demo-user'): UseFlightsResult {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stats = useMemo(() => calculateFlightStats(flights), [flights]);

  const loadFlights = useCallback(async () => {
    if (!userId) {
      setFlights([]);
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      const data = await getFlights();
      setFlights(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load flights');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  const addFlight = useCallback(
    async (input: CreateFlightInput): Promise<Flight | null> => {
      setError(null);
      try {
        const flight = await createFlight(userId, input);
        setFlights((prev) => [...prev, flight]);
        return flight;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add flight');
        return null;
      }
    },
    [userId]
  );

  const removeFlight = useCallback(async (flightId: string): Promise<void> => {
    setError(null);
    try {
      await deleteFlight(flightId);
      setFlights((prev) => prev.filter((f) => f.id !== flightId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete flight');
    }
  }, []);

  const updateFlightDetails = useCallback(
    async (flightId: string, input: UpdateFlightInput): Promise<Flight | null> => {
      setError(null);
      try {
        const updatedFlight = await updateFlight(flightId, input);
        setFlights((prev) => prev.map((f) => (f.id === flightId ? updatedFlight : f)));
        return updatedFlight;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update flight');
        return null;
      }
    },
    []
  );

  useEffect(() => {
    loadFlights();
  }, [loadFlights]);

  return {
    flights,
    stats,
    isLoading,
    error,
    addFlight,
    removeFlight,
    updateFlightDetails,
    refresh: loadFlights,
  };
}
